import { useCallback, useState } from 'react'

import { tenant } from '../../lib/api/tenant.ts'
import { formatDayClock } from '../../lib/format.ts'
import { agentHref } from '../../lib/logs/investigation.ts'
import { Link } from '../../lib/router.tsx'
import { useDebounced } from '../../lib/useDebounced.ts'
import { useResource } from '../../lib/useResource.ts'
import { PageHeader, Toolbar } from '../../components/layout/PageHeader.tsx'
import { IsolationBadge } from '../../components/machines/ReleaseHost.tsx'
import { ShortId } from '../../components/primitives/CopyValue.tsx'
import { SearchInput, SelectField } from '../../components/primitives/Field.tsx'
import { Pagination } from '../../components/primitives/Pagination.tsx'
import { EmptyState, ErrorState, LoadingRow } from '../../components/primitives/States.tsx'
import { TABLE, TD, TH, THEAD, TR } from '../../components/primitives/table.ts'

const PAGE_SIZE = 50

type IsolationFilter = '' | 'true' | 'false'

const ISOLATION_OPTIONS: ReadonlyArray<{ value: IsolationFilter; label: string }> = [
  { value: '', label: 'todas' },
  { value: 'true', label: 'só isoladas' },
  { value: 'false', label: 'só não isoladas' },
]

/**
 * Máquinas da empresa — SOMENTE LEITURA (`GET /api/tenant/machines`, §5.9).
 *
 * Sem criar, editar, remover nem comandar: cadastro e resposta são da Black
 * Ghost. Máquina removida nunca aparece ao cliente, então não há filtro de
 * removidas. O `id` da linha é o `endpoint_id` da telemetria.
 */
export function TenantMachines() {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const search = useDebounced(searchInput)
  const [isolated, setIsolated] = useState<IsolationFilter>('')

  const fetcher = useCallback(
    (signal: AbortSignal) =>
      tenant.machines.list(
        {
          page,
          pageSize: PAGE_SIZE,
          search,
          isolated: isolated === '' ? undefined : isolated === 'true',
        },
        signal,
      ),
    [page, search, isolated],
  )
  const { data, error, loading, reload } = useResource(fetcher)

  return (
    <>
      <PageHeader title="Máquinas" />

      <Toolbar>
        <SearchInput
          label="Buscar máquina"
          placeholder="trecho do fingerprint"
          value={searchInput}
          onValueChange={(value) => {
            setSearchInput(value)
            setPage(1)
          }}
          className="w-64"
        />
        <SelectField
          label="Rede"
          value={isolated}
          onValueChange={(value) => {
            setIsolated(value as IsolationFilter)
            setPage(1)
          }}
          options={ISOLATION_OPTIONS}
          className="w-40"
        />
        {loading && <span className="text-micro text-fg-muted">carregando…</span>}
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {error !== null ? (
          <ErrorState error={error} onRetry={reload} />
        ) : data === null ? (
          <LoadingRow />
        ) : data.items.length === 0 ? (
          <EmptyState
            title="Nenhuma máquina encontrada."
            hint={search === '' ? undefined : `Nada corresponde a “${search}”.`}
          />
        ) : (
          <table className={TABLE}>
            <thead className={THEAD}>
              <tr>
                <th scope="col" className={TH}>
                  Nome
                </th>
                <th scope="col" className={TH}>
                  Fingerprint
                </th>
                <th scope="col" className={TH}>
                  endpoint_id
                </th>
                <th scope="col" className={TH}>
                  Rede
                </th>
                <th scope="col" className={TH}>
                  Criada
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((machine) => (
                <tr key={machine.id} className={TR}>
                  <th scope="row" className={`${TD} max-w-0 font-normal`}>
                    <Link
                      href={agentHref(machine.id, {})}
                      className="block truncate text-fg underline-offset-2 hover:underline"
                      title="Logs desta máquina"
                    >
                      {machine.displayName ?? <span className="italic text-fg-muted">sem nome</span>}
                    </Link>
                  </th>
                  <td className={`${TD} max-w-0`}>
                    <span className="block truncate font-mono text-fg-muted" title={machine.fingerprint}>
                      {machine.fingerprint}
                    </span>
                  </td>
                  <td className={`${TD} whitespace-nowrap`}>
                    <ShortId id={machine.id} label="endpoint_id" />
                  </td>
                  <td className={TD}>
                    {machine.isolation?.is_isolated === true ? (
                      <IsolationBadge machine={machine} />
                    ) : (
                      <span className="text-micro text-fg-muted">
                        {machine.isolation == null ? '—' : 'conectada'}
                      </span>
                    )}
                  </td>
                  <td className={`${TD} tnum whitespace-nowrap`}>{formatDayClock(machine.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data !== null && data.items.length > 0 && (
        <Pagination
          page={data.page}
          pageSize={data.pageSize}
          totalCount={data.totalCount}
          totalPages={data.totalPages}
          busy={loading}
          onPageChange={setPage}
        />
      )}
    </>
  )
}
